import React, { useState, useEffect } from "react";
import { UTILS } from "../config/API";

const RadioField = ({ field, formData, handleChange, picklistCache, setPicklistCache }) => {
  const [radioOptions, setRadioOptions] = useState([]);
  const [isDropdownLoading, setIsDropdownLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch radio options when the component mounts or when itemvaluegroup changes
  useEffect(() => {
    const fetchRadioOptions = async () => {
      if (field.fieldtype === "radio" && field.itemvaluegroup) {
        // Check if options are already cached
        if (picklistCache[field.itemvaluegroup]) {
          setRadioOptions(Array.isArray(picklistCache[field.itemvaluegroup]) 
            ? picklistCache[field.itemvaluegroup] 
            : []);
          return;
        }

        setIsDropdownLoading(true);
        setError(null);
        
        try {
          const response = await fetch(UTILS, {
            method: "POST",
            headers: { 
              "Content-Type": "application/x-www-form-urlencoded",
              "Accept": "application/json"
            },
            body: new URLSearchParams({
              action: "idv",
              s: field.itemvaluegroup,
            }),
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();
          
          // Validate the response data is an array
          if (!Array.isArray(data)) {
            throw new Error("API response is not an array");
          }

          setRadioOptions(data);
          setPicklistCache((prevCache) => ({
            ...prevCache,
            [field.itemvaluegroup]: data,
          }));
        } catch (error) {
          console.error("Error fetching radio options:", error);
          setError(error.message);
          setRadioOptions([]); // Ensure it's always an array
        } finally {
          setIsDropdownLoading(false);
        }
      }
    };

    fetchRadioOptions();
  }, [field.fieldtype, field.itemvaluegroup, field.fieldname, picklistCache, setPicklistCache]);

  // Safe rendering of radio options
  const renderRadioOptions = () => {
    if (!Array.isArray(radioOptions)) {
      return <p className="text-red-500">Invalid options format</p>;
    }

    if (radioOptions.length === 0) {
      return <p className="text-gray-500">No options available</p>;
    }

    return radioOptions.map((option) => {
      // Ensure option has required properties
      if (!option || typeof option !== 'object' || !option.valuetext) {
        return null;
      }

      return (
        <label key={option.valuetext} className="flex items-center">
          <input
            type="radio"
            name={field.fieldname}
            value={option.valuetext}
            checked={formData[field.fieldname] === option.valuetext}
            onChange={handleChange}
            className="w-5 h-5 border-2 border-gray-200 rounded-full focus:ring-2 focus:ring-[#711515] focus:border-transparent"
          />
          <span className="ml-2 text-gray-700">{option.valuetext}</span>
        </label>
      );
    });
  };

  return (
    <div className="flex items-center space-x-4">
      {isDropdownLoading ? (
        <p>Loading options...</p>
      ) : error ? (
        <p className="text-red-500">Error: {error}</p>
      ) : (
        renderRadioOptions()
      )}
    </div>
  );
};

export default RadioField;